import { useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { classNames, useAppConfig } from "@ct-react/core";
import { LocaleLink, useLocaleContext } from "@ct-react/locale";
import { extractCmsContent } from "@shared/graphql";
import { Wrapper } from "../layout/wrapper";
import "./services-list.scss";

const transDefs = defineMessages({
  sub: { id: "services.kicker", defaultMessage: "Nos services" },
  title: {id: "services.title", defaultMessage: "Pour chaque besoin, une solution" }
});

const SERVICES_LIST_GQL_DATA = gql`
  query SubServices($templateId: UUID!) {
    pages: cmsPages(templateId: $templateId, association: "services", asSummary: true) {
      id createdAt data
    }
  }
`;

type Link = { href: string, label: string, icon?: string };

const ServicesList = ({ className, exclusion }: { className?: string, exclusion?: string }) => {

  const intl = useIntl();
  const { locale } = useLocaleContext();
  const { options: { cmsSubPageTemplateId: templateId } } = useAppConfig();

  const { data } = useQuery<{ pages: any[] }>(SERVICES_LIST_GQL_DATA, {
    ssr: true,
    variables: { templateId }
  });

  const items = useMemo<Link[]>(() => {
    const pages = (data?.pages || [])
      .filter(({ id }) => id !== exclusion)
      .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    return pages
      .map(({ data }) => extractCmsContent(data, locale))
      .map(content => ({
        href: `/services/${content["nav-path"]}/`,
        label: content["nav-label"],
        ...(!!content["nav-icon"]) && { icon: content["nav-icon"].originalUrl },
      }));
  }, [ data, locale, exclusion ]);

  const wrapperClasses = classNames("cms-foot services", className);

  if (items.length === 0)
    return null;

  return (
    <section className={wrapperClasses}>
      <Wrapper.Centered className="content">
        <div className="header">
          <span className="kicker">{intl.formatMessage(transDefs.sub)}</span>
          <h2>{intl.formatMessage(transDefs.title)}</h2>
        </div>
        <ul>
          {items.map((item, index) => (
            <li key={index}>
              <LocaleLink to={item.href}>
                {!!item.icon && <img src={item.icon} alt={item.label}/>}
                {item.label}
              </LocaleLink>
            </li>)
          )}
        </ul>
      </Wrapper.Centered>
    </section>)
}

export default ServicesList;
